
import { ListingFilters, Listing, ListingsResponse } from '@/lib/types/listingTypes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listingsApi } from './listingsApi';

interface UseListingsOptions {
  staleTime?: number;
  gcTime?: number;
  keepPreviousData?: boolean;
  refetchOnWindowFocus?: boolean;
}

/**
 * React Query hook for fetching listings with proper caching
 */
export function useListings(
  filters: ListingFilters = {}, 
  options: UseListingsOptions = {}
) {
  const queryClient = useQueryClient();
  
  // Use the filters as part of the query key to ensure proper caching
  return useQuery({
    queryKey: ['listings', filters],
    queryFn: async () => {
      const response = await listingsApi.getListings(filters);
      
      console.log("Listings API response:", response);
      
      // Cache each listing individually for quicker access
      if (response?.data && Array.isArray(response.data)) {
        response.data.forEach((property: Listing) => {
          queryClient.setQueryData(['listing', property.id.toString()], property);
        });
      }
      
      // Create a proper response format mapping from the API response
      const responseData: ListingsResponse = {
        properties: Array.isArray(response?.data) ? response.data : [],
        pagination: response?.meta || {
          totalItems: 0,
          totalPages: 1,
          currentPage: 1,
          itemsPerPage: 10
        }
      };
      
      return responseData;
    },
    staleTime: options.staleTime || 60 * 1000, // Default 1 minute
    gcTime: options.gcTime || 300 * 1000, // Default 5 minutes
    retry: 1, // Retry once on error
    keepPreviousData: options.keepPreviousData ?? true,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
  });
}

/**
 * React Query hook for fetching a single listing by ID
 */
export function useListing(id: string) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: ['listing', id],
    queryFn: async () => {
      // Try to get from cache first if it exists
      const cachedListing = queryClient.getQueryData<Listing>(['listing', id]);
      if (cachedListing) {
        return { listing: cachedListing };
      }
      
      // Otherwise fetch from API
      const response = await listingsApi.getListing(id);
      
      // Handle both response formats
      if (response?.data?.listing) {
        return { listing: response.data.listing };
      } else if (response?.data) {
        return { listing: response.data };
      }
      
      throw new Error("Invalid API response format");
    },
    staleTime: 300 * 1000, // 5 minutes
    retry: 1, // Retry once on error
  });
}

export { listingsApi };
